<script lang="ts">
  import * as Dialog from '$lib/components/ui/dialog';
  import AttachedExerciseCard from './AttachedExerciseCard.svelte';
  import type { MessageAttachment } from './api';
  import { fileIconClass, formatFileSize } from './attachments';

  // Shared by MessageBubble.svelte and CommentItem.svelte -- the image/video/audio/exercise-card
  // switch was byte-identical in both. The outer margin wrapper stays with each caller (their
  // margins differ slightly), so this only renders the {#each} contents.
  export let attachments: MessageAttachment[];

  // Videos already get a fullscreen affordance for free (native <video controls>); images don't,
  // so clicking one opens it larger in this lightbox instead. Content is styled full-bleed
  // (transparent/borderless/no padding) rather than the usual card look -- see the class override
  // below -- since this is a media viewer, not a form dialog.
  let lightboxUrl: string | null = null;
  $: lightboxOpen = lightboxUrl != null;

  function closeLightbox() {
    lightboxUrl = null;
  }
</script>

{#each attachments as attachment}
  {#if attachment.type === 'image'}
    <button type="button" class="block cursor-zoom-in p-0" on:click={() => (lightboxUrl = attachment.url ?? null)}>
      <img class="max-h-60 max-w-full rounded-md" src={attachment.url} alt="attachment" />
    </button>
  {:else if attachment.type === 'video'}
    <video class="max-h-60 max-w-full rounded-md" src={attachment.url} controls></video>
  {:else if attachment.type === 'audio'}
    <audio src={attachment.url} controls></audio>
  {:else if attachment.type === 'file'}
    <a
      href={attachment.url}
      download={attachment.name ?? true}
      target="_blank"
      rel="noopener noreferrer"
      class="flex max-w-[220px] items-center gap-2.5 rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground hover:bg-muted/70"
      title="Download {attachment.name ?? 'attachment'}"
    >
      <i class="fa-solid {fileIconClass(attachment.name)} shrink-0 text-lg text-muted-foreground"></i>
      <span class="min-w-0 flex-1">
        <span class="block truncate font-medium">{attachment.name ?? 'Attachment'}</span>
        {#if attachment.size}
          <span class="block text-xs text-muted-foreground">{formatFileSize(attachment.size)}</span>
        {/if}
      </span>
      <i class="fa-solid fa-arrow-down-to-line shrink-0 text-xs text-muted-foreground"></i>
    </a>
  {:else if attachment.type === 'activity' || attachment.type === 'workout'}
    <AttachedExerciseCard type={attachment.type} path={attachment.path} />
  {/if}
{/each}

<Dialog.Root portal="#shadcn-ui-dialog-target" open={lightboxOpen} onOpenChange={(open) => !open && closeLightbox()}>
  <Dialog.Content class="max-w-[95vw] translate-x-[-50%] translate-y-[-50%] border-0 bg-transparent p-0 shadow-none">
    <img src={lightboxUrl} alt="attachment" class="max-h-[90vh] max-w-full rounded-md object-contain" />
  </Dialog.Content>
</Dialog.Root>
